import React, { useEffect, useState } from 'react';
import type {
  DeregisterValidationFn,
  FormBlurEventFn,
  FormChangeEventFn,
  FormValidationResult,
  HasErrorFn,
  RegisterValidationFn,
  SetFormValueFn,
} from '@noths/polaris-client-react-hooks';
import { Button } from '@noths/polaris-design-system-core-components';
import type { Dictionary } from '@noths/polaris-dev-ts-recipes';

import { ErrorMessage } from 'src/components/atoms/ErrorMessage/ErrorMessage';
import { WeblinkInput } from 'src/components/atoms/WeblinkInput/WeblinkInput';
import {
  AttachmentUploader,
  DEFAULT_UPLOAD_LABEL as ATTACHMENT_UPLOADER_LABEL,
} from 'src/components/molecules/AttachmentUploader/components/AttachmentUploader';
import { InitialAttachment } from 'src/components/molecules/AttachmentUploader/hooks/useAttachmentUpload';
import { RadioButtonGroup } from 'src/components/molecules/RadioButtonGroup/RadioButtonGroup';
import {
  LEADIN_TIME_OPTIONS,
  MIME_TYPES,
} from 'src/components/organisms/Signup/constants/aboutYourBusinessOptions';
import {
  HOW_SOON_START_SELLING_COPY,
  PACKAGING_UPLOAD_DESCRIPTION_COPY,
  PACKAGING_UPLOAD_LABEL_COPY,
  YOUR_WEBSITE_DESCRIPTION_COPY,
  YOUR_WEBSITE_LABEL_COPY,
} from 'src/components/organisms/Signup/constants/copy';
import { MAX_WEBSITE_LENGTH } from 'src/components/organisms/Signup/constants/fieldLengthConstraints';
import type { FormValidationErrors } from 'src/components/organisms/Signup/types';
import {
  ABOUT_YOUR_BUSINESS_VALIDATION_RULE_NAMES,
  PACKAGING_ATTACHMENTS_VALIDATION_RULE_NAMES,
} from 'src/components/organisms/Signup/validation';
import { event as trackEvent } from 'src/hooks/useTracking/tracking';
import { getFeatureFlags } from 'src/settings';
import * as styles from 'src/styles/styles';
import type {
  AttachmentUploadComplete,
  AttachmentUploadError,
  AttachmentUploadInProgress,
  LeadInTime,
  RegistrationForm,
} from 'src/types/registration';
import { AttachmentState } from 'src/types/registration';
import { DeliverAreas } from './DeliverAreas';
import { PipelinePlans } from './PipelinePlans';
import { getDynamicOnlineStoreOptionName, SalesChannels } from './SalesChannels';
import { SocialMedia } from './SocialMedia';

const DATA_SECTION = '2';

export interface AboutYourBusinessProps {
  clearError: (errorName: string) => void;
  deregisterValidation: DeregisterValidationFn;
  errors: FormValidationErrors;
  formValidator: (subsectionValidationRuleNames?: string[]) => FormValidationResult<Dictionary>;
  formValues: RegistrationForm;
  hasError: HasErrorFn;
  onBlur: FormBlurEventFn;
  onFormChange: FormChangeEventFn;
  pageBack: () => boolean;
  pageForward: () => boolean;
  registerValidation: RegisterValidationFn<RegistrationForm>;
  setError: (errorName: string, errorMessage: string) => void;
  setFormValue: SetFormValueFn<RegistrationForm>;
}

const getValidatableFields = (form: RegistrationForm) => {
  const onlineStoreFields = form.salesChannels.onlineStores.storesUsed.map(({ id }) =>
    getDynamicOnlineStoreOptionName(id),
  );

  return [...ABOUT_YOUR_BUSINESS_VALIDATION_RULE_NAMES, ...onlineStoreFields];
};

const trackUploadEvent = (action: string) => {
  trackEvent({
    action,
    label: ATTACHMENT_UPLOADER_LABEL,
    fieldDetails: {
      form_section: `Section ${DATA_SECTION}`,
      field_position: '6',
      field_type: 'button',
      field_name: ATTACHMENT_UPLOADER_LABEL,
      required_field: true,
    },
  });
};

const getInitialUploads = (formValues: RegistrationForm) =>
  formValues.packagingAttachments.map(
    (attachment) => new InitialAttachment(attachment.attachmentId, attachment.label),
  );

export const AboutYourBusiness = ({
  clearError,
  deregisterValidation,
  errors,
  formValidator,
  formValues,
  hasError,
  onBlur,
  onFormChange,
  pageBack,
  pageForward,
  registerValidation,
  setError,
  setFormValue,
}: AboutYourBusinessProps) => {
  const formContainsErrors = Boolean(Object.keys(errors).length);

  const [uploadCompleted, setUploadCompleted] = useState<
    AttachmentUploadComplete | AttachmentUploadError | null
  >(null);

  useEffect(() => {
    if (uploadCompleted) {
      const filteredAttachements = formValues.packagingAttachments.filter(
        (attachment) => attachment.attachmentId !== uploadCompleted.attachmentId,
      );

      let attachments = filteredAttachements;
      if (uploadCompleted.attachmentState === AttachmentState.UPLOAD_COMPLETE) {
        attachments = [...filteredAttachements, uploadCompleted];
      }

      setFormValue('packagingAttachments', attachments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadCompleted]);

  useEffect(() => {
    if (attachmentError()) {
      formValidator(PACKAGING_ATTACHMENTS_VALIDATION_RULE_NAMES);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues.packagingAttachments]);

  const handleClickNavigation = (forward = true) => {
    const { isValid } = formValidator(getValidatableFields(formValues));

    if (isValid) {
      if (forward) {
        pageForward();
      } else {
        pageBack();
      }
    }
  };

  const handleAttachmentUploadStarted = (attachmentId: string, filename: string) => {
    const attachment: AttachmentUploadInProgress = {
      attachmentState: AttachmentState.UPLOADING,
      attachmentId,
      label: filename,
    };

    setFormValue('packagingAttachments', [...formValues.packagingAttachments, attachment]);
  };

  const handlAttachmentUploadedCompleted = (
    attachmentId: string,
    filename: string,
    uploadReference: string,
  ) => {
    const attachment: AttachmentUploadComplete = {
      attachmentState: AttachmentState.UPLOAD_COMPLETE,
      attachmentId,
      label: filename,
      uploadReference,
    };

    setUploadCompleted(attachment);

    trackUploadEvent('Photo Upload Success');
  };

  const handlAttachmentUploadError = (attachmentId: string, filename: string) => {
    const attachment: AttachmentUploadError = {
      attachmentState: AttachmentState.UPLOAD_ERROR,
      attachmentId,
      label: filename,
    };

    setUploadCompleted(attachment);
  };

  const handleAttachmentRemoved = (attachmentId: string) => {
    const filteredAttachements = formValues.packagingAttachments.filter(
      (attachment) => attachment.attachmentId !== attachmentId,
    );

    setFormValue('packagingAttachments', filteredAttachements);
  };

  const isNotUploadedPackagingAttachment = (fileName: string) => {
    const fileAlreadyAttached = formValues.packagingAttachments.some(
      (attachment: { label: string }) => attachment.label === fileName,
    );

    if (fileAlreadyAttached) {
      return `A file named '${fileName}' has already been attached to this application`;
    }
    return null;
  };

  const attachmentError = () =>
    (errors.packagingAttachments as string | undefined) ||
    (errors['packagingAttachments.notUploaded'] as string | undefined);

  return (
    <section css={styles.pageSection}>
      <h2 css={styles.sectionHeading}>Section 2</h2>
      <h3 css={styles.sectionTitle}>Tell us about your business</h3>
      <div css={styles.subSection}>
        <WeblinkInput
          css={styles.textField}
          data-position="1"
          data-section="2"
          description={YOUR_WEBSITE_DESCRIPTION_COPY}
          error={errors.website as string | undefined}
          id="partner-company-website"
          label={YOUR_WEBSITE_LABEL_COPY}
          maxLength={MAX_WEBSITE_LENGTH}
          name="website"
          onBlur={onBlur}
          onChange={onFormChange}
          onWeblinkError={(message) => setError('website', message)}
          required
          value={formValues.website}
        />
      </div>
      <div css={styles.subSection}>
        <SocialMedia
          data-position="2"
          data-section="2"
          deregisterValidation={deregisterValidation}
          errors={errors}
          formValues={formValues}
          onBlur={onBlur}
          onFormChange={onFormChange}
          registerValidation={registerValidation}
          setError={setError}
          setFormValue={setFormValue}
        />
      </div>
      <div css={styles.subSection}>
        <RadioButtonGroup
          accessibleLabel={HOW_SOON_START_SELLING_COPY}
          additionalCss={styles.grouped}
          error={errors.leadinTime as string | undefined}
          name="leadinTime"
          onBlur={onBlur}
          onChange={(leadinTime) => setFormValue('leadinTime', leadinTime as LeadInTime)}
          radioButtonData={LEADIN_TIME_OPTIONS}
          required
          value={formValues.leadinTime}
        />
      </div>
      <div css={styles.subSection}>
        <SalesChannels
          clearError={clearError}
          deregisterValidation={deregisterValidation}
          errors={errors}
          formValues={formValues}
          hasError={hasError}
          onBlur={onBlur}
          onFormChange={onFormChange}
          registerValidation={registerValidation}
          setFormValue={setFormValue}
        />
      </div>
      <div css={styles.subSection}>
        <DeliverAreas
          clearError={clearError}
          errors={errors}
          formValues={formValues}
          onBlur={onBlur}
          onFormChange={onFormChange}
          setFormValue={setFormValue}
        />
      </div>
      {(getFeatureFlags().uploadSectionsEnabled ||
        window.localStorage.getItem('forceUploadSections') === 'true') && (
        <div css={styles.subSection}>
          <label css={styles.label} htmlFor="fake-upload-dialog-for-now">
            {PACKAGING_UPLOAD_LABEL_COPY}
          </label>
          <p css={styles.descriptionPara}>{PACKAGING_UPLOAD_DESCRIPTION_COPY}</p>
          <AttachmentUploader
            error={attachmentError()}
            initialAttachments={getInitialUploads(formValues)}
            label={ATTACHMENT_UPLOADER_LABEL}
            maxUploads={40}
            mimeTypes={MIME_TYPES}
            onAttachmentRemoved={handleAttachmentRemoved}
            onUploadButtonClick={() => trackUploadEvent('Photo Upload CTA')}
            onUploadCompleted={handlAttachmentUploadedCompleted}
            onUploadError={handlAttachmentUploadError}
            onUploadStarted={handleAttachmentUploadStarted}
            required
            shouldUploadAttachment={isNotUploadedPackagingAttachment}
          />
        </div>
      )}
      <div css={styles.subSection}>
        <PipelinePlans
          clearError={clearError}
          errors={errors}
          formValues={formValues}
          onBlur={onBlur}
          onFormChange={onFormChange}
          setFormValue={setFormValue}
        />
      </div>
      {Object.keys(errors).length > 0 && <ErrorMessage />}
      <div css={styles.navigationButtonsGrid}>
        <Button
          disabled={formContainsErrors}
          id="back-from-page-about-your-business"
          onClick={() => handleClickNavigation(false)}
          secondary
        >
          Back
        </Button>
        <Button
          disabled={formContainsErrors}
          id="next-from-page-about-your-business"
          onClick={() => handleClickNavigation()}
          secondary
        >
          Next
        </Button>
      </div>
    </section>
  );
};
